import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BaseGetAll,
  BaseResponse,
  GenericList,
} from '../../shared/commons/generic.responses';
import { endpoints } from '../../shared/apis/endpoints';
import { Observable, map, of } from 'rxjs';
import {
  ChargesResume,
  DeleteMultiStopReq,
  DrayCalculation,
  MultiStopResponse,
  Order,
  OrderFilters,
  OrderPaymentDetail,
  PrintTicketStopsConsignee,
  QuickConsigneePost,
  QuickCustomerPost,
  QuickLesseePost,
  QuickRampPost,
  QuickShipperPost,
  RegisterMultiStopReq,
} from '../models/order';
import { IOrder } from '../interfaces/order.interface';
import { Accesorial } from '../models/accesorial';
import { Appointment } from '../models/appointment';
import { AccesorialBelongingEnum } from '../utils/enums';
import { DriverAccesorial, DriverPay } from '../models/driver';
import { getNonNullProps } from '../../shared/functions/getNonNulls';
import { buildQueryParam } from '../../shared/functions/queryBuilder';
import { Storage } from '../models/storage';
import { Preference } from '../models/preference';
import { Rate } from '../models/rates';
import { Tracking } from '../models/tracking';
import { NotifyDriverReq } from '../models/communication';
import { PierAppointmentNotification } from '../models/pier-appointment-notification';
import { parseDate } from '../../shared/functions/parseDate';
import { format } from 'date-fns';
import { toLocalISOString } from '../../shared/functions/date-helper';
@Injectable({
  providedIn: 'root',
})
export class OrderService {
  constructor(private _http: HttpClient) { }

  //#region --> CATALOGS

  GenericCatalogSearch(
    searchParam: string,
    queryCmmd: string,
  ): Observable<GenericList[]> {
    if (searchParam?.length < 2) return of([]);

    const url = `${endpoints.GENERIC_CATALOG_SEARCH}?SearchParam=${searchParam}&QueryCommand=${queryCmmd}`;

    return this._http.get<BaseResponse<GenericList[]>>(url).pipe(
      map((res) => {
        return res?.body?.result;
      }),
    );
  }

  GenericGetAll(queryCmmd: string): Observable<BaseGetAll<GenericList[]>> {
    return this._http.get<BaseGetAll<GenericList[]>>(
      `${endpoints.GENERIC_GET_ALL}?q=${queryCmmd}`,
    );
  }

  SearchCatalog(
    searchParam: string,
    endpoint: string,
  ): Observable<BaseResponse<GenericList[]>> {
    return this._http.get<BaseResponse<GenericList[]>>(
      `${endpoint}?SearchParam=${searchParam}`,
    );
  }

  GetPreferences(): Observable<BaseGetAll<Preference>> {
    return this._http.get<BaseGetAll<Preference>>(endpoints.GET_PREFERENCES);
  }

  SearchCityRampLocation(
    searchedText: string,
  ): Observable<BaseGetAll<GenericList[]>> {
    return this._http.get<BaseGetAll<GenericList[]>>(
      `${endpoints.SEARCH_CITY_RAMP_LOCATION}/${searchedText}`,
    );
  }

  //#endregion

  //#region --> ORDERS

  SaveOrder(req: Order): Observable<BaseResponse<any>> {
    return this._http.post<any>(
      endpoints.SAVE_ORDER,
      JSON.parse(JSON.stringify(req)),
    );
  }

  setOrderAsInvoiced(proId: number): Observable<BaseGetAll<any>> {
    return this._http.put<BaseGetAll<any>>(endpoints.SET_ORDER_INVOICED, {
      proId: proId,
    });
  }

  setListOrdersAsInvoiced(lstProId: number[]): Observable<BaseGetAll<any>> {
    return this._http.put<BaseGetAll<any>>(
      endpoints.SET_ORDERS_INVOICED,
      lstProId.map((a) => ({ proId: a })),
    );
  }

  sendEmailToCustomer(req: any): Observable<BaseResponse<any>> {
    return this._http.post<BaseResponse<any>>(
      endpoints.SEND_EMAIL_TO_CUSTOMER,
      JSON.parse(JSON.stringify(req)),
    );
  }

  download_invoice(proId: number) {
    return this._http.get(
      `${endpoints.DOWNLOAD_INVOICE}?proID=${proId}`, {responseType:'blob'});
  }

  EditOrder(req: any): Observable<BaseResponse<any>> {
    return this._http.put<BaseResponse<any>>(
      endpoints.EDIT_ORDER,
      JSON.parse(JSON.stringify(req)),
    );
  }

  FilterOrders(filters: OrderFilters): Observable<BaseGetAll<IOrder[]>> {
    const data = filters.buildRequest();
    const req = buildQueryParam(filters);

    return this._http.get<BaseGetAll<IOrder[]>>(endpoints.FILTER_ORDERS, {
      params: req,
    });
  }

  DrayCalculation(req: Order): Observable<BaseGetAll<DrayCalculation>> {
    var q = `${endpoints.DRAY_CALCULATION}?`;

    q += `CustomerId=${req.customerId}&`;

    if (
      !isNaN(req.shipperId) &&
      req.shipperId != null &&
      req.shipperId != undefined
    )
      q += `ShipperId=${req.shipperId}&`;

    if (
      !isNaN(req.consigneeId) &&
      req.consigneeId != null &&
      req.consigneeId != undefined
    )
      q += `ConsigneeId=${req.consigneeId}&`;

    q += `Order_Type=${req.orderTypeId}&`;
    q += `Origination_Ramp=${req.originationRamp}&`;
    q += `Termination_Ramp=${req.terminationRamp}&`;
    if (!isNaN(req.ProID) && req.ProID != null && req.ProID != undefined)
      q += `ProID=${req.ProID}`;

    return this._http.get<BaseGetAll<DrayCalculation>>(q);
  }

  GetOrderPaymentDetail(
    proId: number,
    customerId: number,
  ): Observable<BaseGetAll<OrderPaymentDetail[]>> {
    return this._http.get<BaseGetAll<OrderPaymentDetail[]>>(
      `${endpoints.GET_ORDER_PAYMENTS}?CustomerId=${customerId}&ProId=${proId}`,
    );
  }

  GetChargesResume(proIds: number[]): Observable<BaseGetAll<ChargesResume[]>> {
    const queryString: string = proIds.map((x) => `ProIds=${x}`).join('&');

    return this._http.get<BaseGetAll<ChargesResume[]>>(
      `${endpoints.GET_CHARGES_RESUME}?${queryString}`,
    );
  }

  AttachImagesToTheOrder(req: any): Observable<any> {
    return this._http.post<any>(endpoints.UPLOAD_IMAGES, req);
  }

  GetOrderImages(proId: number): Observable<BaseGetAll<string[]>> {
    return this._http.get<BaseGetAll<string[]>>(
      `${endpoints.GET_IMAGES}/${proId}`,
    );
  }

  DeleteOrderImages(picture: string): Observable<BaseGetAll<any>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.request<BaseGetAll<any>>(
      'delete',
      endpoints.DELETE_IMAGES,
      { body: { picture }, headers: headers },
    );
  }

  GetTicketStopsConsignees(
    proIds: number[],
  ): Observable<BaseGetAll<PrintTicketStopsConsignee[]>> {
    const queryString: string = proIds.map((x) => `ProIds=${x}`).join('&');

    return this._http.get<BaseGetAll<PrintTicketStopsConsignee[]>>(
      `${endpoints.TICKET_STOPS_CONSIGNEES}?${queryString}`,
    );
  }

  //#endregion

  //#region --> QUICK POSTS

  QuickCustomerPost(req: QuickCustomerPost): Observable<BaseGetAll<number>> {
    return this._http.post<BaseGetAll<number>>(endpoints.SAVE_CUSTOMER, req);
  }

  QuickShipperPost(req: QuickShipperPost): Observable<BaseGetAll<number>> {
    return this._http.patch<BaseGetAll<any>>(endpoints.PATCH_SHIPPER, req);
  }

  QuickConsigneePost(req: QuickConsigneePost): Observable<BaseGetAll<number>> {
    return this._http.patch<BaseGetAll<number>>(endpoints.PATCH_CONSIGNEE, req);
  }

  QuickBoxSizePost(boxSize1: string) {
    return this._http.patch<BaseGetAll<number>>(endpoints.SAVE_BOXSIZE, {
      boxSize1,
    });
  }

  QuickLesseePost(req: QuickLesseePost): Observable<BaseGetAll<any>> {
    return this._http.patch<BaseGetAll<any>>(endpoints.PACTH_LESSEE, req);
  }

  QuickRampPost(req: QuickRampPost): Observable<BaseGetAll<number>> {
    return this._http.patch<BaseGetAll<number>>(endpoints.RAMP, req);
  }

  GetRates(): Observable<BaseGetAll<Rate[]>> {
    return this._http.get<BaseGetAll<Rate[]>>(endpoints.GET_RATES);
  }

  QuickAccesorialPost(
    accesorialName: string,
    belogs: 'DRIVER' | 'STORAGE' | 'ORDER',
  ) {
    const req = {
      descr: accesorialName,
      belongDriver: belogs == 'DRIVER' ? true : false,
      belongStorage: belogs == 'STORAGE' ? true : false,
      belongOrder: belogs == 'ORDER' ? true : false,
      percMargin: 0,
    };

    return this._http.patch<BaseGetAll<number>>(
      endpoints.PATCH_ACCESORIAL,
      req,
    );
  }

  //#endregion

  //#region --> ACCESORIAL

  SaveAccesorials(req: Accesorial): Observable<BaseGetAll<number>> {
    var request = {
      proId: req.proId,
      accesorialsId: +req.accesorialsId,
      descr: req.descr,
      amount: req.amount,
      orderAccesorialsId: req.orderAccesorialsId,
    };
    return this._http.post<BaseGetAll<number>>(
      endpoints.SAVE_ACCESORIALS,
      request,
    );
  }

  GetAccesorialBelonging(
    belongsTo: AccesorialBelongingEnum,
  ): Observable<BaseGetAll<GenericList[]>> {
    return this._http.get<BaseGetAll<GenericList[]>>(
      `${endpoints.ACCESORIAL_BELONGING}?accessorialType=${belongsTo}`,
    );
  }

  GetAccesorialsByProID(proId: number): Observable<BaseGetAll<Accesorial[]>> {
    return this._http.get<BaseGetAll<Accesorial[]>>(
      `${endpoints.GET_ACCESORIALS_BY_PROID}/${proId}`,
    );
  }

  DeleteOrderAccesorial(
    proId: number,
    orderAccesorialId: number,
  ): Observable<BaseGetAll<any>> {
    return this._http.delete<BaseGetAll<any>>(
      `${endpoints.DELETE_ORDER_ACCESORIAL}/${proId}/${orderAccesorialId}`,
    );
  }

  DeleteDriverAccesorial(
    proId: number,
    driverAccesorialId: number,
  ): Observable<BaseGetAll<any>> {
    return this._http.delete<BaseGetAll<any>>(
      `${endpoints.DELETE_DRIVER_ACCESORIAL}/${proId}/${driverAccesorialId}`,
    );
  }

  GetAppPreferences(): Observable<BaseGetAll<any>> {
    return this._http.get<BaseGetAll<any>>(`${endpoints.PREFERENCE}`).pipe(
      map((res) => {
        res.body.fuelPctOfDray = res.body.fuelPctOfDray * 100;
        return res;
      }),
    );
  }

  UpdatePreferences(req: any): Observable<BaseGetAll<any>> {
    return this._http.put<BaseGetAll<any>>(`${endpoints.PREFERENCE}`, req);
  }

  //#endregion

  //#region --> APPOINTMENT

  SaveAppointment(appt: Appointment): Observable<BaseResponse<any>> {
    const req = appt.buildRequest();
    return this._http.put<BaseResponse<any>>(endpoints.SAVE_APPOINTMENT, req);
  }

  //#endregion

  //#region --> DRIVER

  GetDriverPayAndDetailByProID(
    proId: number,
  ): Observable<BaseGetAll<DriverPay[]>> {
    return this._http
      .get<
        BaseGetAll<DriverPay[]>
      >(`${endpoints.GET_DRIVERPAY_BY_PROID}/${proId}`)
      .pipe(
        map((res) => {
          res.body.forEach(function(x: DriverPay) {
            x.total = x.dray + x.driverAccAmount;
            x.startDate = x.startDate == null ? null :  toLocalISOString(new Date(x.startDate));
            x.endDate =  x.endDate == null ? null : toLocalISOString(new Date(x.endDate));
            x.driverAccessorials.forEach(function(x: DriverAccesorial) {
              x.amountPercentage = +(x.amountPercentage * 100).toFixed(2);
            });
          });

          return res;
        }),
      );
  }

  SaveDriverPay(req: DriverPay): Observable<BaseGetAll<number>> {
    // returns driverSeqNum
    const request = {
      driverSeqNum: req.driverSeqNum,
      driverId: req.driverId,
      proId: req.proId,
      seqNum: req.seqNum,
      startDate: (req.startDate != null) ?  new Date(req.startDate).toISOString().split('T')[0] + 'T00:00:00' : null,
      endDate: (req.startDate != null) ? new Date(req.endDate).toISOString().split('T')[0] + 'T00:00:00' : null,
      dray: req.dray,
      lastActivity: req.lastActivity,
      startCityId: req.startCityId,
      endCityId: req.endCityId,
      startRamp: req.startRamp,
      endRamp: req.endRamp,
      startStorageLocation: req.startStorageLocation,
      endStorageLocation: req.endStorageLocation,
    };
    return this._http.post<BaseGetAll<number>>(
      endpoints.SAVE_DRIVERPAY,
      getNonNullProps(request),
    );
  }

  GetDriverAccesorialsBySeqNum(
    driverSeqNum: number,
  ): Observable<BaseGetAll<DriverAccesorial[]>> {
    return this._http
      .get<
        BaseGetAll<DriverAccesorial[]>
      >(`${endpoints.GET_DRIVERACC_BY_SEQNUM}/${driverSeqNum}`)
      .pipe(
        map((res) => {
          res.body.forEach(function(x: DriverAccesorial) {
            x.amountPercentage = +x.amountPercentage.toFixed(2);
          });

          return res;
        }),
      );
  }

  SaveDriverAccesorial(
    req: DriverAccesorial,
    proID: number,
  ): Observable<BaseGetAll<number>> {
    const request = {
      driverSeqNum: req.driverSeqNum,
      proID: proID,
      amountOrder: req.amountOrder,
      driverAccesorialsID: req?.driverAccesorialsID,
      accesorialID: +req.accesorialID,
      descr: req.descr,
      amount: req.amount,
    };

    console.log(request);

    return this._http.post<BaseGetAll<number>>(
      endpoints.SAVE_DRIVERACC,
      request,
    );
  }

  DeleteDriverPay(
    proId: number,
    driverSeqNum: number,
  ): Observable<BaseGetAll<any>> {
    return this._http.delete<BaseGetAll<any>>(
      `${endpoints.DELETE_DRIVERPAY}/${proId}/${driverSeqNum}`,
    );
  }

  //#endregion

  //#region --> STORAGE

  GetStorage(proId: number): Observable<BaseGetAll<Storage[]>> {
    return this._http
      .get<BaseGetAll<Storage[]>>(`${endpoints.STORAGE}/${proId}`)
      .pipe(
        map((res) => {
          res.body.forEach(
            function(x: Storage) {
              x.totalDays = this.CalculStorageDays(
                x.dateIn.toString(),
                x.dateOut.toString(),
              );
              x.datePaid = x.datePaid; // because it is only date not datetime
              x.dateOut = x.dateOut;
              x.dateIn = x.dateIn;
            }.bind(this),
          );

          return res;
        }),
      );
  }

  CalculStorageDays(sDateIn?: string, sDateOut?: string): number {
    console.log('Log dentro', sDateIn, sDateOut);
    var dateIn = new Date(sDateIn);
    var dateOut = new Date(sDateOut);
    // Verificar que dateIn y dateOut sean objetos Date válidos
    if (
      isNaN(dateIn.getTime()) ||
      isNaN(dateOut.getTime()) ||
      sDateIn == null ||
      sDateOut == null
    ) {
      return 0;
    }
    // Ajustar la hora de dateIn a las 00:00:00 y dateOut a las 23:59:59
    var startOfDayIn = new Date(dateIn.setHours(0, 0, 0, 0));
    var endOfDayOut = new Date(dateOut.setHours(23, 59, 59, 999));

    // Calcular la diferencia en milisegundos
    var diffMilliseconds = Math.abs(dateOut.getTime() - dateIn.getTime());

    // Convertir la diferencia de milisegundos a días y redondear hacia arriba
    var diffDays = Math.ceil(diffMilliseconds / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  //   console.log(fechaSeleccionada.toISOString())
  SaveStorage(req: Storage, proId: number): Observable<BaseGetAll<number>> {
    // let dateIn = format(new Date(req.dateIn), "yyyy-MM-dd HH:mm");// req.dateIn ? new Date(req.dateIn).toISOString().split('T')[0] + "T" + new Date(req.dateIn).toISOString().split('T')[1]  : null;
    // let dateOut =  req.dateOut ? new Date(req.dateOut).toISOString().slice(0, 19).replace('T', ' ') : null;
    // format(new Date(event), "yyyy-MM-dd HH:mm");
    const request = {
      storageLocation: req.location,
      proId: proId,
      dateIn: req.dateIn ? req.dateIn : null,
      dateOut: req.dateOut ? req.dateOut : null,
      storageAmt: req.storageAmount,
      amountPaid: req.amountPaid,
      amountOrder: req.storageAmount,
      storageOrderId: req.storageOrderId,
      datePaid: req.datePaid,
      ratePerDayUsed: req.ratePerDay,
      storageRatePerDayForClientUsed: req.storageRatePerDayForClientUsed,
      type: req.type,
      description: req.description,
    };

    return this._http.patch<BaseGetAll<number>>(endpoints.STORAGE, request);
  }

  DeleteStorage(
    prodId: number,
    storageOrderId: number,
  ): Observable<BaseGetAll<any>> {
    return this._http.delete<BaseGetAll<any>>(
      `${endpoints.STORAGE}/${prodId}/${storageOrderId}`,
    );
  }

  //#endregion

  // #region -->  MULTISTOPS

  GetMultiStops(prodId: number): Observable<BaseGetAll<MultiStopResponse[]>> {
    return this._http.get<BaseGetAll<MultiStopResponse[]>>(
      `${endpoints.GET_MULTI_STOPS}/${prodId}`,
    );
  }

  MutateMultiStop(
    proId: number,
    req: RegisterMultiStopReq[],
  ): Observable<BaseGetAll<any>> {
    return this._http.patch<BaseGetAll<any>>(
      `${endpoints.MUTATE_MULTI_STOPS}/${proId}`,
      req,
    );
  }

  DeleteMultiStop(req: DeleteMultiStopReq): Observable<BaseGetAll<boolean>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._http.request<BaseGetAll<boolean>>(
      'delete',
      endpoints.DELETE_MULTI_STOPS,
      { body: req, headers: headers },
    );
  }

  //#endregion

  //#region --> TRACKING

  GetOrderTracking(proId: number): Observable<BaseGetAll<Tracking[]>> {
    return this._http
      .get<BaseGetAll<Tracking[]>>(`${endpoints.TRACKING}/${proId}`)
      .pipe(
        map((res) => {
          res.body.forEach(function(x: any) {
            x.containerCheck = JSON.parse(x.containerCheck);
            x.documentDetails = JSON.parse(x.documentDetails);
          });
          return res;
        }),
      );
  }

  //#endregion

  //#region --> COMMUNICATION

  NotifyDriver(req: NotifyDriverReq): Observable<BaseGetAll<boolean>> {
    return this._http.post<BaseGetAll<boolean>>(endpoints.NOTIFY_DRIVER, req);
  }

  NotifyPierAppointment(req: PierAppointmentNotification): Observable<BaseGetAll<boolean>> {
    return this._http.post<BaseGetAll<boolean>>(endpoints.NOTIFY_PIER_APPT, req.buildRequest());
  }

  //#endregion
}
